import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";

// Served from the frontend bundle (public/preview/) — no backend round-trip,
// no CORS, browser caches them aggressively via the hashed build output.
const DEFAULT_PAGES = ["/preview/P1.png", "/preview/P2.png", "/preview/P3.png", "/preview/P4.png"];

// Preload all pages on first mount so navigation is instant.
const _preloadedSets = new Set();
function preloadPages(pages) {
  if (typeof window === "undefined") return;
  const key = pages.join("|");
  if (_preloadedSets.has(key)) return;
  _preloadedSets.add(key);
  pages.forEach((src) => {
    const img = new Image();
    img.src = src;
  });
}

export default function BookPreviewModal({ open, onClose, onCta, pages }) {
  const PAGES = Array.isArray(pages) && pages.length > 0 ? pages : DEFAULT_PAGES;
  const [active, setActive] = useState(0);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setActive(0);
      setImgError(false);
      preloadPages(PAGES);
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open, PAGES]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setActive((i) => (i + 1) % PAGES.length); // RTL: left = next
      if (e.key === "ArrowRight") setActive((i) => (i - 1 + PAGES.length) % PAGES.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const next = () => setActive((i) => (i + 1) % PAGES.length);
  const prev = () => setActive((i) => (i - 1 + PAGES.length) % PAGES.length);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
      data-testid="book-preview-modal"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#040914]/92 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Modal panel */}
      <div className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto bg-[#0B1D3A] border border-[#C5A059]/40 rounded-sm shadow-2xl shadow-black/70 fade-up">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 left-3 z-10 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/5 transition"
          aria-label="إغلاق"
          data-testid="preview-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Title */}
        <div className="px-6 sm:px-10 pt-9 pb-4 text-center">
          <div className="inline-block text-[#C5A059] text-[10px] sm:text-xs tracking-[0.35em] mb-3">
            معاينة حصرية
          </div>
          <h3 className="font-display text-xl sm:text-2xl lg:text-3xl font-black text-white leading-snug">
            شاهد {PAGES.length} صفحات من داخل الكتاب قبل الشراء
          </h3>
        </div>

        {/* Stacked pages mockup */}
        <div className="relative px-3 sm:px-12 pt-2 pb-6">
          <div
            className="relative mx-auto"
            style={{ width: "100%", maxWidth: "420px" }}
          >
            {/* Soft glow behind the stack */}
            <div className="absolute inset-0 bg-[radial-gradient(closest-side,rgba(197,160,89,0.18),transparent_70%)] blur-2xl -z-10" />

            {/* Front (active) page — uses image's natural aspect ratio so the
                full page is always visible on mobile (no cropping). */}
            <div
              className="relative w-full rounded-[3px] overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.7),0_0_0_1px_rgba(197,160,89,0.25)] bg-[#fdfbf6]"
              data-testid="preview-active-page"
            >
              {imgError ? (
                <div className="w-full aspect-[3/4] flex flex-col items-center justify-center p-6 text-center text-[#040914] gap-3" data-testid="preview-img-error">
                  <div className="text-[#C5A059] text-3xl">📖</div>
                  <p className="text-sm font-bold">جاري تحميل الصفحة...</p>
                  <button
                    onClick={() => setImgError(false)}
                    className="text-xs underline text-[#040914]/70"
                  >
                    إعادة المحاولة
                  </button>
                </div>
              ) : (
                <img
                  key={active}
                  src={PAGES[active]}
                  alt={`صفحة ${active + 1} من الكتاب`}
                  className="w-full h-auto block select-none"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  onError={() => setImgError(true)}
                  loading="eager"
                />
              )}
            </div>

            {/* Page nav arrows (overlay) */}
            <button
              onClick={prev}
              aria-label="السابق"
              data-testid="preview-prev"
              className="absolute right-1 sm:-right-8 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#0B1D3A]/90 border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059] hover:text-[#040914] transition flex items-center justify-center shadow-lg"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              aria-label="التالي"
              data-testid="preview-next"
              className="absolute left-1 sm:-left-8 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#0B1D3A]/90 border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059] hover:text-[#040914] transition flex items-center justify-center shadow-lg"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          </div>

          {/* Dots indicator */}
          <div className="flex items-center justify-center gap-1.5 mt-5">
            {PAGES.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                aria-label={`اذهب إلى الصفحة ${i + 1}`}
                data-testid={`preview-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === active ? "w-6 bg-[#C5A059]" : "w-1.5 bg-white/25 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
          <div className="text-center text-[11px] text-[#A1B0C6] mt-2">
            {active + 1} / {PAGES.length}
          </div>
        </div>

        {/* CTA */}
        <div className="px-6 sm:px-10 pb-8 pt-3" dir="rtl">
          <p className="text-center text-sm sm:text-base text-white/90 leading-relaxed mb-4">
            إذا وجدت هذه العينة مفيدة، فالكتاب الكامل يحتوي على أمثلة وتطبيقات أوسع <span className="text-[#C5A059] font-bold">+ النسخة الصوتية مجاناً</span>
          </p>
          <Button
            onClick={onCta}
            data-testid="preview-cta"
            className="w-full bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] hover:text-[#040914] font-black text-base sm:text-lg py-7 rounded-sm shadow-[0_0_40px_rgba(197,160,89,0.4)] cta-pulse"
          >
            احصل على الكتاب الآن
          </Button>
          <p className="mt-3 text-center text-[11px] text-[#A1B0C6]">
            تسليم فوري · ضمان الخصوصية · دفع آمن
          </p>
        </div>
      </div>
    </div>
  );
}
